import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { OrdemDeServicoCompleta } from '../../types/ordemServico';
import { OsHeaderSection } from './OsHeaderSection';
import { OsInformacoesGeraisSection } from './OsInformacoesGeraisSection';
import { OsLogisticaVeiculoSection } from './OsLogisticaVeiculoSection';
import { OsRepassesDespesasSection } from './OsRepassesDespesasSection';
import { OsAssinaturaSection } from './OsAssinaturaSection';
import { OsTotaisSection } from './OsTotaisSection';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface OrdemDeServicoFormViewProps {
  osId: number | null;
  onClose?: () => void;
  isDrawer?: boolean;
}

export const OrdemDeServicoFormView: React.FC<OrdemDeServicoFormViewProps> = ({
  osId,
  onClose,
  isDrawer = false,
}) => {
  const [os, setOs] = useState<OrdemDeServicoCompleta | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (osId) {
      fetchOS();
    } else {
      setOs(null);
    }
  }, [osId]);

  const fetchOS = async () => {
    if (!osId) return;
    setLoading(true);
    setErrorMsg(null);

    try {
      // 1. Fetch main OS
      const { data: mainData, error: mainError } = await supabase
        .from('ordem_de_servico')
        .select('*')
        .eq('id', osId)
        .single();

      if (mainError) throw mainError;

      const fullOs: OrdemDeServicoCompleta = { ...mainData };

      // 2. Fetch Status
      if (mainData.status_id) {
        const { data: statusData } = await supabase
          .from('status')
          .select('id, status, cor')
          .eq('id', mainData.status_id)
          .single();
        if (statusData) fullOs.status = statusData;
      }

      // 3. Fetch Pedido e Cliente
      if (mainData.pedido_id) {
        const { data: pedidoData } = await supabase
          .from('pedido')
          .select('id, pessoa_id')
          .eq('id', mainData.pedido_id)
          .single();

        if (pedidoData) {
          fullOs.pedido = pedidoData;
          if (pedidoData.pessoa_id) {
            const { data: pessoaData } = await supabase
              .from('pessoa')
              .select('id, nome, documento')
              .eq('id', pedidoData.pessoa_id)
              .single();
            if (pessoaData) {
              fullOs.pedido.pessoa = pessoaData;
              fullOs.cliente_nome = pessoaData.nome;
            }
          }
        }
      }

      // 4. Fetch Tipo Veículo
      if (mainData.tipo_veiculo_id) {
        const { data: tipoVeiculoData } = await supabase
          .from('tipo_veiculo')
          .select('id, tipo_veiculo')
          .eq('id', mainData.tipo_veiculo_id)
          .single();
        if (tipoVeiculoData) fullOs.tipo_veiculo = tipoVeiculoData;
      }

      // 5. Fetch Veículo e Motorista/Agregado
      if (mainData.veiculos_id) {
        const { data: veiculoData } = await supabase
          .from('veiculos')
          .select('*')
          .eq('id', mainData.veiculos_id)
          .single();

        if (veiculoData) {
          fullOs.veiculos = veiculoData;

          if (veiculoData.motorista_id) {
            const { data: motData } = await supabase
              .from('pessoa')
              .select('id, nome')
              .eq('id', veiculoData.motorista_id)
              .single();
            if (motData) {
              fullOs.veiculos.motorista = motData;
              fullOs.motorista_nome = motData.nome;
            }
          }

          if (veiculoData.agregado_id) {
            const { data: agrData } = await supabase
              .from('pessoa')
              .select('id, nome')
              .eq('id', veiculoData.agregado_id)
              .single();
            if (agrData) {
              fullOs.veiculos.agregado = agrData;
              fullOs.agregado_nome = agrData.nome;
            }
          }
        }
      }

      // 6. Fetch Item Serviço
      if (mainData.item_servico) {
        const { data: detData } = await supabase
          .from('detalhes_do_pedido')
          .select('*')
          .eq('id', mainData.item_servico)
          .single();

        if (detData) {
          fullOs.fk_item_servico = detData;
          if (detData.tipo_servico_id) {
            const { data: tsData } = await supabase
              .from('tipo_servico')
              .select('tipo')
              .eq('id', detData.tipo_servico_id)
              .single();
            if (tsData) {
              fullOs.fk_item_servico.tipo_servico = tsData;
            }
          }
        }
      }

      // 7. Fetch Item Ajudante
      if (mainData.item_ajudante) {
        const { data: detAjud } = await supabase
          .from('detalhes_do_pedido')
          .select('*')
          .eq('id', mainData.item_ajudante)
          .single();
        if (detAjud) fullOs.fk_item_ajudante = detAjud;
      }

      setOs(fullOs);
    } catch (err: any) {
      console.error('Erro ao buscar detalhes da OS:', err);
      setErrorMsg(err.message || 'Erro ao carregar os dados da ordem de serviço.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!osId) return null;

  const contentMarkup = (
    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xl max-w-4xl w-full mx-auto my-auto max-h-[88vh] flex flex-col overflow-y-auto space-y-6 print:max-h-none print:shadow-none print:p-0 print:overflow-visible">
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-500 gap-3">
          <Loader2 className="w-8 h-8 text-red-600 animate-spin" />
          <span className="text-sm font-medium">Carregando detalhes da O.S #{osId}...</span>
        </div>
      ) : errorMsg ? (
        <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
          <p className="text-sm font-bold text-red-800">Falha ao buscar O.S</p>
          <p className="text-xs text-red-600">{errorMsg}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition-colors mt-2"
            >
              Fechar
            </button>
          )}
        </div>
      ) : os ? (
        <div className="space-y-6">
          {/* Seção 1: Cabeçalho & Status */}
          <OsHeaderSection os={os} onClose={onClose} onPrint={handlePrint} />

          {/* Seção 2: Informações Gerais da O.S */}
          <OsInformacoesGeraisSection os={os} />

          {/* Seção 3: Veículo, Logística e Valores */}
          <OsLogisticaVeiculoSection os={os} />

          {/* Seção 4: Repasses e Despesas */}
          <OsRepassesDespesasSection os={os} />

          {/* Seção 5: Assinatura */}
          <OsAssinaturaSection os={os} />

          {/* Seção 6: Totais */}
          <OsTotaisSection os={os} />
        </div>
      ) : null}
    </div>
  );

  if (isDrawer) {
    return (
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-3xl bg-white shadow-2xl overflow-y-auto p-4 sm:p-6 print:static print:max-w-none print:w-full print:p-0">
        {contentMarkup}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6 overflow-hidden print:p-0 print:bg-white print:block">
      {contentMarkup}
    </div>
  );
};
